import { useState, useEffect } from 'react';
import { isRestricted } from './restrict';
import { setCookie, getCookie, deleteCookie } from './dom';
import { urlify } from './string';

// Matches the opacity transitions in the notification stylesheets.
const EXIT_DURATION = 300;
const DEFAULT_COOKIE_DURATION = 365;

const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const notificationKey = (item) => urlify(item.name);

// Server-side there's no document, so fall back to the request cookies passed through setup.
const readCookie = (name, setup) => (typeof document === 'undefined' ? setup?.cookies?.[name] : getCookie(name));

const isDismissed = (item, setup) => !!readCookie(notificationKey(item), setup);

const isAllowedForCountry = (item, setup) => !item.countryRestrict || !isRestricted(setup, { selectedCountries: item.countryRestrict });

const hasContent = (n) => !!(n.text || n.title || n.image || n.images || n.film || n.zohoFields);

const filterActiveNotifications = (content = [], setup) => content.filter((n) => n.active && hasContent(n) && isAllowedForCountry(n, setup));

const splitCampaigns = (campaign, setup) => {
	const active = filterActiveNotifications(campaign, setup);
	return {
		scrolling: active.filter((n) => n.scrolls),
		cards: active.filter((n) => !n.scrolls),
	};
};

/*
	Shared state for notifications that live in the bottom-right stack (cards and popups).
	- expanded: cards start collapsed; popups (autoExpand) open once their delay has elapsed,
	  unless the visitor minimized them earlier (remembered in a '<name>_minimized' cookie).
	- dismiss: stores the dismissal cookie for cookieDuration and fades the notification out.
	  With `persistent` it collapses to its button instead (the consumer type choice, which stays
	  available to change).
	- enabled: holds back the auto-expand delay (popups wait for a cookie-consent decision).
*/
const useNotificationState = (item, { setup, autoExpand = false, enabled = true, persistent = false } = {}) => {
	const name = notificationKey(item);
	const minimizedCookie = `${name}_minimized`;
	const cookieDuration = item.cookieDuration || DEFAULT_COOKIE_DURATION;

	const [expanded, setExpanded] = useState(() => autoExpand
		&& !item.delay
		&& !readCookie(minimizedCookie, setup)
		&& !(persistent && isDismissed(item, setup)));
	const [status, setStatus] = useState(() => (!persistent && isDismissed(item, setup) ? 'removed' : 'visible'));

	useEffect(() => {
		if (!autoExpand || !item.delay || !enabled) return undefined;
		const timer = setTimeout(() => {
			if (!getCookie(minimizedCookie)) setExpanded(true);
		}, parseInt(item.delay, 10) * 1e3);
		return () => clearTimeout(timer);
	}, [enabled]);

	useEffect(() => {
		if (status !== 'exiting') return undefined;
		const timer = setTimeout(() => setStatus('removed'), EXIT_DURATION);
		return () => clearTimeout(timer);
	}, [status]);

	const expand = () => {
		if (autoExpand) deleteCookie(minimizedCookie);
		setExpanded(true);
	};

	const minimize = () => {
		if (autoExpand) setCookie(minimizedCookie, 1, cookieDuration);
		setExpanded(false);
	};

	const dismiss = (value = 1) => {
		setCookie(name, value, cookieDuration);
		if (persistent) setExpanded(false);
		else setStatus('exiting');
	};

	return {
		name,
		expanded,
		visible: status !== 'removed',
		exiting: status === 'exiting',
		expand,
		minimize,
		toggle: () => (expanded ? minimize() : expand()),
		dismiss,
	};
};

/*
	Keeps keyboard focus inside a modal while `active`.
	- autoFocus: move focus in on open (and back to the previously focused element on close).
	  Without it, focus is only pulled in on the visitor's first Tab press.
	- initialFocus: selector for the element to focus first, instead of the first focusable one.
	- onEscape: called on Escape, for modals that can be closed.
*/
const useFocusTrap = (containerRef, active, { autoFocus = true, initialFocus, onEscape } = {}) => {
	useEffect(() => {
		const container = containerRef.current;
		if (!active || !container) return undefined;

		const previousFocus = document.activeElement;
		const getFocusable = () => [...container.querySelectorAll(FOCUSABLE)];
		const getInitial = () => (initialFocus && container.querySelector(initialFocus)) || getFocusable()[0];

		if (autoFocus) {
			const initial = getInitial();
			if (initial) initial.focus();
		}

		const onKeyDown = (e) => {
			if (e.key === 'Escape') {
				if (onEscape) onEscape();
				return;
			}
			if (e.key !== 'Tab') return;

			const els = getFocusable();
			if (!els.length) {
				e.preventDefault();
				return;
			}
			const first = els[0];
			const last = els[els.length - 1];

			if (!container.contains(document.activeElement)) {
				e.preventDefault();
				(e.shiftKey ? last : getInitial()).focus();
			} else if (e.shiftKey && document.activeElement === first) {
				e.preventDefault();
				last.focus();
			} else if (!e.shiftKey && document.activeElement === last) {
				e.preventDefault();
				first.focus();
			}
		};

		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('keydown', onKeyDown);
			if (autoFocus && previousFocus && previousFocus.focus) previousFocus.focus();
		};
	}, [active]);
};

export { FOCUSABLE, notificationKey, isDismissed, isAllowedForCountry, filterActiveNotifications, splitCampaigns, useNotificationState, useFocusTrap };
